// The Action Owner filter, a section of the screen's filter menu
// (`FilterMenu.tsx`), which shows the filter as a tag while it is on and holds
// Clear filter.
//
// A barrier grid can be narrowed to the barriers one person is Action Owner of,
// chosen with the same picker the form uses. Only Action Owner counts. The
// multi-valued `Owner` column is the source's and plays no part, which the users
// decided.
//
// WITH THE FILTER OFF, THE PICKER OFFERS THE SIGNED-IN USER. It opens holding
// them, with Apply beside it, because their own barriers are what most people
// filter for. Nothing is narrowed until Apply: a grid opens on everyone, and
// opening the panel to look does not change it. Choosing somebody from the
// search narrows to them at once, as before, and taking the offered person out
// leaves the picker empty for a search rather than closing the panel.
//
// THE FILTER LIVES IN THE PAGE ADDRESS, as `?owner=`, so that it survives a
// reload and can be bookmarked or sent to someone:
//
// | `owner` | means |
// | --- | --- |
// | absent | everyone |
// | `me` | whoever is signed in -- a link sent to someone shows them theirs |
// | an object id | that person, by the id that survives a rename |
//
// Anything else is ignored rather than sent: the API would refuse it, and a
// mistyped address should show the grid rather than an error about a filter
// nobody chose.

import { useId, useState } from 'react';
import { PeoplePicker, type PersonValue } from '../crud/PeoplePicker';

export const OWNER_PARAM = 'owner';
const ME = 'me';

export type OwnerFilter = { kind: 'everyone' } | { kind: 'me' } | { kind: 'person'; objectId: string };

const OBJECT_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function parseOwner(raw: string | null): OwnerFilter {
  if (raw === ME) return { kind: 'me' };
  if (raw && OBJECT_ID.test(raw)) return { kind: 'person', objectId: raw.toLowerCase() };
  return { kind: 'everyone' };
}

export function ownerParam(filter: OwnerFilter): string | null {
  if (filter.kind === 'me') return ME;
  if (filter.kind === 'person') return filter.objectId;
  return null;
}

/** What a grid with nothing to show says, naming the filter that emptied it. */
export function emptyMessage(search: string, filter: OwnerFilter, personName: string): string {
  const base = search ? `No barrier matches ${search}` : 'No barriers in this view';
  if (filter.kind === 'everyone') return `${base}.`;
  return `${base} with ${filter.kind === 'me' ? 'you' : personName} as Action Owner.`;
}

/** What the filter's tag says, or null while it is off. */
export function ownerLabel(filter: OwnerFilter, personName: string): string | null {
  if (filter.kind === 'everyone') return null;
  return `Action Owner: ${personName}`;
}

export interface ActionOwnerFilterProps {
  filter: OwnerFilter;
  /** The signed-in user's object id, once `/api/me` has answered. */
  myObjectId: string | null;
  /** The signed-in user's name, for the person the picker offers. */
  myName: string;
  /** The name to show for the filter: the signed-in user's, or the person's. */
  name: string;
  onChange: (next: OwnerFilter) => void;
}

export function ActionOwnerFilter({ filter, myObjectId, myName, name, onChange }: ActionOwnerFilterProps) {
  const labelId = useId();
  // Whether the signed-in user has been taken out of the picker, while the
  // filter is off. The section is drawn afresh each time the panel opens, so
  // they are offered again next time.
  const [declined, setDeclined] = useState(false);
  const offered = filter.kind === 'everyone' && myObjectId !== null && !declined;

  // An object id stands in until the name is known, so the picker shows a
  // person rather than marking the filter as a name that matches nobody.
  const value: PersonValue =
    filter.kind !== 'everyone'
      ? { objectId: filter.kind === 'me' ? (myObjectId ?? ME) : filter.objectId, name }
      : offered
        ? { objectId: myObjectId, name: myName }
        : { objectId: null, name: null };

  return (
    <div className="owner-filter">
      <span className="filter-field-label" id={labelId}>
        Action Owner
      </span>
      <PeoplePicker
        labelledBy={labelId}
        value={value}
        onChange={(next) => {
          if (!next.objectId) {
            if (filter.kind === 'everyone') setDeclined(true);
            else onChange({ kind: 'everyone' });
          }
          // Choosing yourself is the same filter as Apply, so the address
          // reads `me` rather than an id either way.
          else if (myObjectId && next.objectId.toLowerCase() === myObjectId.toLowerCase()) onChange({ kind: 'me' });
          else onChange({ kind: 'person', objectId: next.objectId.toLowerCase() });
        }}
      />
      {offered && (
        <button type="button" className="primary filter-apply" data-autofocus onClick={() => onChange({ kind: 'me' })}>
          Apply
        </button>
      )}
    </div>
  );
}
